import { Button, Notice } from '@rental/ui'
import { type RefObject, useEffect, useState } from 'react'
import { useLocale } from '../i18n'
import { problemText, type StaffProblem } from '../problems'

/** What the focus can land on in what came back: the target itself, or the first control in it. */
const FOCUSABLE =
  'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * A list a form chooses from that did not load (the categories, the places,
 * a category's extras, a point's Zs), said where its control stands: what is
 * missing, why, and a way to ask again. Without it the control offers less
 * than there is, in silence. Once the list comes, the notice goes with its
 * Riprova, and the focus goes to what it fills (`target`): the target itself
 * when it takes the focus, or the first control in it. Moved once what came
 * is drawn (UI-2g): a list that comes with controls of its own has none to
 * take the focus while it is still on its way.
 */
export function LookupProblem({
  problem,
  what,
  busy,
  retry,
  target,
  retryFor,
}: {
  problem: StaffProblem | null
  /** What did not load, as a sentence: "Le categorie non si sono caricate." */
  what: string
  busy: boolean
  retry: () => Promise<{ isSuccess: boolean }>
  target: RefObject<HTMLElement | null>
  /**
   * The list's place, said in Riprova's name where two lists of one kind share
   * a screen (Cassa's Zs, UI-2g).
   */
  retryFor?: string
}) {
  const { locale, messages: m } = useLocale()
  // Riprova pressed: the problem it answers, until its answer is drawn. React
  // Query clears the error the moment a fetch with nothing to show starts
  // (status `pending`), long before the list comes: the notice stays until then,
  // its button busy, and the focus moves only once nothing is on its way (S9's gate).
  const [retrying, setRetrying] = useState<StaffProblem | null>(null)
  useEffect(() => {
    if (problem || busy || !retrying) return
    setRetrying(null)
    const into = target.current
    // A heading the focus is moved to by script (tabindex -1) takes it too.
    const takes = into?.matches(`${FOCUSABLE}, [tabindex="-1"]`)
    ;(takes ? into : into?.querySelector<HTMLElement>(FOCUSABLE))?.focus()
  }, [problem, busy, retrying, target])
  const shown = problem ?? retrying
  if (!shown) return null
  return (
    <Notice tone="warning">
      <p>
        {what} {problemText(shown, locale, m)}
      </p>
      <Button
        variant="link"
        className="self-start"
        busy={busy}
        onClick={async () => {
          setRetrying(shown)
          const answer = await retry()
          if (!answer.isSuccess) setRetrying(null)
        }}
      >
        {m.app.retry}
        {retryFor ? <span className="sr-only">, {retryFor}</span> : null}
      </Button>
    </Notice>
  )
}
